import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Type, UploadCloud, Check } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { applyAppearance } from "@/components/AppearanceLoader";

export default function FontSettings() {
  const [a, setA] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { adminApi.getAppearance().then(setA).catch(() => {}); }, []);

  const save = async (patch) => {
    setSaving(true);
    try { const next = await adminApi.saveAppearance(patch); setA(next); applyAppearance(next); toast.success("Font updated across the app"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };
  const uploadFont = (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Font file must be under 3 MB");
    const r = new FileReader();
    r.onload = () => save({ heartstrings_font_data: String(r.result), font: "heartstrings" });
    r.readAsDataURL(file);
  };

  if (!a) return null;
  return (
    <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="font-settings">
      <div className="mb-4 flex items-center gap-2">
        <Type className="h-5 w-5 text-[#D46117]" />
        <div>
          <h2 className="font-serif text-xl font-bold text-[#28282E]">Global font</h2>
          <p className="text-xs text-[#28282E]/70">Applies to every screen — landing page, catalogue, orders, events and admin.</p>
        </div>
        {saving && <Loader2 className="ml-auto h-4 w-4 animate-spin text-[#28282E]/50" />}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {a.fonts.map((f) => {
          const active = a.font === f.key;
          const needsUpload = f.custom && !a.heartstrings_font_data;
          return (
            <button key={f.key} data-testid={`font-option-${f.key}`} disabled={saving || needsUpload} onClick={() => save({ font: f.key })}
              className={`relative rounded-2xl border p-4 text-left transition ${active ? "border-[#D46117] ring-2 ring-[#EBBAD1]" : "border-slate-200 hover:border-[#EBBAD1]"} ${needsUpload ? "opacity-60" : ""}`}>
              {!f.custom && <link rel="stylesheet" href={f.css_url} />}
              <div className="text-2xl font-bold text-[#28282E]" style={{ fontFamily: f.family }}>Aa Bb Cc 123</div>
              <div className="mt-1 text-sm font-semibold text-[#28282E]">{f.label}{(f.key === "general_sans" || f.key === "heartstrings") && <span className="ml-1 rounded-full bg-[#FBEEF4] px-1.5 py-0.5 text-[9px] font-bold uppercase text-[#D46117]">Primary</span>}</div>
              {needsUpload && <div className="mt-1 text-[11px] text-amber-700">Upload the font file below to enable</div>}
              {active && <span className="absolute right-3 top-3 grid h-5 w-5 place-items-center rounded-full bg-[#D46117] text-white"><Check className="h-3 w-3" /></span>}
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl bg-slate-50 p-3">
        <Label className="text-xs font-semibold text-[#28282E]/70">Heartstrings font file (.woff2 / .woff / .ttf / .otf){a.heartstrings_font_data ? " — uploaded" : ""}</Label>
        <input ref={fileRef} type="file" accept=".woff2,.woff,.ttf,.otf,font/*" className="hidden" data-testid="heartstrings-file-input" onChange={(e) => { uploadFont(e.target.files?.[0]); e.target.value = ""; }} />
        <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="heartstrings-upload-button" className="rounded-full border-[#EBBAD1]"><UploadCloud className="mr-2 h-4 w-4" /> {a.heartstrings_font_data ? "Replace file" : "Upload Heartstrings"}</Button>
      </div>
    </div>
  );
}
